import { useApi } from '@bumble/app/hooks/use-api';
import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Indicator } from '@bumble/app/components/Indicator';
import getDate from '@bumble/app/utils/get-date';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';

interface RouterFeatureMatrixProps {
  cpeId: string;
}

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Feature',
    flex: 1,
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (value, row) => row.name || row.feature_key,
  },
  {
    field: 'enabled',
    headerName: 'Enabled',
    width: 120,
    disableColumnMenu: true,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
        <Indicator active={!!params.value} />
      </Box>
    ),
  },
  {
    field: 'enforce',
    headerName: 'Enforced',
    width: 120,
    disableColumnMenu: true,
    valueGetter: (value) => (value ? 'Yes' : 'No'),
  },
  {
    field: 'entitlement_scope_type',
    headerName: 'Entitlement Scope',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'usage_count',
    headerName: 'Usage Count',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'last_used_at',
    headerName: 'Last Used',
    width: 200,
    disableColumnMenu: true,
    valueGetter: (value) => (value ? getDate(value) : 'N/A'),
  },
  {
    field: 'expires_at',
    headerName: 'Expires At',
    width: 200,
    disableColumnMenu: true,
    valueGetter: (value) => (value ? getDate(value) : 'N/A'),
  },
];

export const RouterFeatureMatrix = ({ cpeId }: RouterFeatureMatrixProps) => {
  const api = useApi();

  const { isLoading, data } = useQuery(
    ['getCpeFeatureMatrix', cpeId],
    api.getCpeFeatureMatrixApiV1GetCpeFeatureMatrixGet.bind(null, { cpe_id: cpeId }),
    { enabled: !!cpeId, cacheTime: 0 },
  );

  if (isLoading) return <Loading />;
  if (!data?.data) return <NoData />;

  const rows = data.data.map((row, index) => ({ ...row, id: row.feature_key || index }));

  return <DataTableGrid rows={rows} columns={columns} hideSearchBar />;
};
